/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import * as React from 'react';

import { Button, Icon, useBreakpoint, useFlags } from 'sarsaparilla';

import { useAuthState } from 'shared-ui/auth';
import { SearchAutosuggest } from '../../search-autosuggest';
import { QuickSearch } from '../QuickSearch/QuickSearch';

import { CartIndicatorContainer } from './cart/CartIndicatorContainer';

import { MOBILE_MENU_TRIGGER_ID } from './Navbar.utils';

import { CSRMenu } from './csr/CSRMenu';
import { AvatarTriggerButton } from './AvatarTriggerButton';
import { SignInButton } from './SignInButton';
import { MobileMenu } from './MobileMenu';

import styles from './NavbarRight.module.scss';

interface NavbarRightProps {
    hideSearchBar?: boolean;
    isGuestLoginAllowed?: boolean;
}

const NAVBAR_SEARCH_INPUT_ID = 'navbar-search-input';
const SEARCH_INPUT_QUERY_PARAM = 'headerTextQuery';

export function NavbarRight({ hideSearchBar, isGuestLoginAllowed }: NavbarRightProps) {
    const { topNavSearchV2 } = useFlags();
    const [isMobileMenuOpen, setMobileMenuOpen] = React.useState(false);

    const { isLgAndUp } = useBreakpoint();

    // Redux
    const { csrAccount, isGuest, isRegisteredUserOrGuest } = useAuthState();

    const onMobileMenuClick = () => {
        setMobileMenuOpen((prev) => !prev);
    };

    return (
        <>
            <nav className={styles.navRight} aria-label="Top Navigation">
                {hideSearchBar ? null : (
                    <div className={styles.searchBarWrap}>
                        {topNavSearchV2 ? (
                            <QuickSearch />
                        ) : (
                            <div className={styles.navSearchInputWrapper}>
                                <SearchAutosuggest
                                    queryCriteriaPropertyName={SEARCH_INPUT_QUERY_PARAM}
                                    size="md"
                                    id={NAVBAR_SEARCH_INPUT_ID}
                                />
                            </div>
                        )}
                    </div>
                )}

                <div className={styles.linksWrap}>
                    <Button
                        appearance="link"
                        href={process.env.SN_KB_EXT_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={styles.helpLink}
                        id="ga-global-nav-help-link"
                    >
                        Help
                    </Button>

                    <CartIndicatorContainer />

                    {csrAccount ? (
                        <CSRMenu />
                    ) : isRegisteredUserOrGuest ? (
                        <AvatarTriggerButton onClick={onMobileMenuClick} />
                    ) : (
                        <SignInButton isGuestLoginAllowed={isGuestLoginAllowed} />
                    )}

                    <Button
                        appearance="subtle"
                        id={MOBILE_MENU_TRIGGER_ID}
                        className={styles.mobileMenuButton}
                        aria-label="Navigation Menu"
                        clickTagCategory="Global Navigation"
                        clickTagAction="Open Navigation Menu"
                        clickTagLabel="Hamburger Menu Button"
                        aria-owns={isMobileMenuOpen ? 'nav-mobile-menu-id' : undefined}
                        aria-expanded={isMobileMenuOpen}
                        onClick={onMobileMenuClick}
                    >
                        <Icon
                            iconName={
                                isMobileMenuOpen
                                    ? 'close'
                                    : isLgAndUp
                                      ? 'menu'
                                      : 'rec-menu'
                            }
                            size="lg"
                        />
                    </Button>
                </div>
            </nav>

            <MobileMenu
                isOpen={isMobileMenuOpen}
                onClose={onMobileMenuClick}
                isGuestAllowed={isGuestLoginAllowed}
                isGuest={isGuest}
            />
        </>
    );
}
